import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import Swal from 'sweetalert2'

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#4680ff',
  green:  '#2CA87F',
  red:    '#dc3545',
  orange: '#F29E4C',
}

const DB_TYPES = ['MySQL', 'PostgreSQL', 'MSSQL', 'MariaDB']

const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#1a202c', marginBottom: 6 }}>
      {label}{required && <span style={{ color: COLORS.red, marginLeft: 3 }}>*</span>}
    </label>
    {children}
  </div>
)

const inputStyle = (focus) => ({
  width: '100%',
  padding: '8px 12px',
  border: `1.5px solid ${focus ? COLORS.blue : '#e8ecf0'}`,
  borderRadius: 8,
  fontSize: 13,
  color: '#1a202c',
  outline: 'none',
  background: '#fff',
  transition: 'border-color 0.15s',
  boxSizing: 'border-box',
})

const SectionCard = ({ title, icon, color, children }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e8ecf0',
    borderRadius: 12,
    marginBottom: 20,
    overflow: 'hidden',
  }}>
    <div style={{
      padding: '14px 20px',
      borderBottom: '1px solid #e8ecf0',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className={icon} style={{ color, fontSize: 16 }} />
      </div>
      <span style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>{title}</span>
    </div>
    <div style={{ padding: '20px 24px' }}>{children}</div>
  </div>
)

const EMPTY_CONN = {
  db_type: 'MySQL',
  host: '',
  port: '',
  database_name: '',
  username: '',
  password: '',
}

const EMPTY_MAP = {
  table_name: '',
  mobile_field: '',
  points_field: '',
  card_id_field: '',
  app_link: '',
}

export default function LoyaltyLink() {
  const navigate = useNavigate()
  const [conn, setConn]         = useState(EMPTY_CONN)
  const [mapping, setMapping]   = useState(EMPTY_MAP)
  const [columns, setColumns]   = useState([])          // fetched after test
  const [tested, setTested]     = useState(false)       // connection tested successfully
  const [testStatus, setTestStatus] = useState(null)    // null | 'ok' | 'fail'
  const [testMsg, setTestMsg]   = useState('')
  const [testing, setTesting]   = useState(false)
  const [saving, setSaving]       = useState(false)
  const [focus, setFocus]         = useState('')
  const [showPass, setShowPass]   = useState(false)
  const [hasPassword, setHasPassword] = useState(false)

  // Load saved config on mount
  useEffect(() => {
    axiosInstance.get('/loyalty/connection')
      .then(res => {
        if (res.data) {
          const d = res.data
          setHasPassword(!!d.has_password)
          setConn({
            db_type:       d.db_type       || 'MySQL',
            host:          d.host          || '',
            port:          d.port          || '',
            database_name: d.database_name || '',
            username:      d.username      || '',
            password:      '',
          })
          setMapping({
            table_name:    d.table_name    || '',
            mobile_field:  d.mobile_field  || '',
            points_field:  d.points_field  || '',
            card_id_field: d.card_id_field || '',
            app_link:      d.app_link      || '',
          })
        }
      })
      .catch(() => {}) // no saved config yet — silent
  }, [])

  const handleConn = (e) => {
    setConn(p => ({ ...p, [e.target.name]: e.target.value }))
    setTested(false)
    setTestStatus(null)
    setTestMsg('')
    setColumns([])
  }

  const handleMap = (e) => {
    setMapping(p => ({ ...p, [e.target.name]: e.target.value }))
  }

  const handleTest = async () => {
    if (!conn.host || !conn.database_name || !conn.username) {
      Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Please fill Host, Database and Username before testing.', confirmButtonColor: COLORS.blue })
      return
    }
    setTesting(true)
    setTestStatus(null)
    setTestMsg('')
    try {
      const res = await axiosInstance.post('/loyalty/connection/test', { ...conn, table_name: mapping.table_name })
      setTestStatus('ok')
      setTestMsg(res.data?.message || 'Connection successful')
      setTested(true)
      if (res.data?.columns?.length) setColumns(res.data.columns)
    } catch (err) {
      setTestStatus('fail')
      setTestMsg(err?.response?.data?.message || 'Connection failed. Check your credentials.')
      setTested(false)
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    if (!conn.host || !conn.database_name || !conn.username || !mapping.table_name || !mapping.mobile_field || !mapping.points_field) {
      Swal.fire({ icon: 'warning', title: 'Incomplete', text: 'Please fill all required fields and map the mobile and points columns.', confirmButtonColor: COLORS.blue })
      return
    }
    setSaving(true)
    try {
      await axiosInstance.post('/loyalty/connection', { ...conn, ...mapping })
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Loyalty connection settings saved successfully.', confirmButtonColor: COLORS.green })
    } catch {
      Swal.fire({ icon: 'error', title: 'Save failed', text: 'Could not save the configuration. Please try again.', confirmButtonColor: COLORS.red })
    } finally {
      setSaving(false)
    }
  }

  const inp = (name, value, onChange, placeholder, type = 'text', extra = {}) => (
    <input
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      onFocus={() => setFocus(name)}
      onBlur={() => setFocus('')}
      style={inputStyle(focus === name)}
      {...extra}
    />
  )

  const sel = (name, value, onChange, options, placeholder) => (
    <select
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => setFocus(name)}
      onBlur={() => setFocus('')}
      style={{ ...inputStyle(focus === name), background: '#fff' }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  )

  return (
    <div className="pc-container">
      <div className="pc-content">

        {/* Page Header */}
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
          padding: '16px 24px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
            background: `${COLORS.blue}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left" style={{ color: COLORS.blue, fontSize: 18 }} />
          </div>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: `${COLORS.blue}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="ti ti-link" style={{ color: COLORS.blue, fontSize: 20 }} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a202c' }}>Loyalty System Link</div>
            <div style={{ fontSize: 12, color: COLORS.gray }}>Configure the external loyalty database connection and map customer fields</div>
          </div>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          {/* ── Section 1: Connection ── */}
          <SectionCard title="Database Connection" icon="ti ti-database" color={COLORS.blue}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>

              <Field label="Database Type" required>
                {sel('db_type', conn.db_type, handleConn, DB_TYPES)}
              </Field>

              <Field label="Port">
                {inp('port', conn.port, handleConn, 'e.g. 3306 (MySQL) / 5432 (Postgres)')}
              </Field>

              <Field label="Host / IP Address" required>
                {inp('host', conn.host, handleConn, 'e.g. 192.168.1.10 or db.example.com')}
              </Field>

              <Field label="Database Name" required>
                {inp('database_name', conn.database_name, handleConn, 'e.g. loyalty_db')}
              </Field>

              <Field label="Username" required>
                {inp('username', conn.username, handleConn, 'Database username')}
              </Field>

              <Field label="Password">
                <div style={{ position: 'relative' }}>
                  {inp('password', conn.password, handleConn, hasPassword && !conn.password ? '••••••••  (saved — type to change)' : 'Database password', showPass ? 'text' : 'password')}
                  <i
                    className={`ti ${showPass ? 'ti-eye-off' : 'ti-eye'}`}
                    onClick={() => setShowPass(p => !p)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      color: COLORS.gray, cursor: 'pointer', fontSize: 16,
                    }}
                  />
                </div>
                {hasPassword && !conn.password && (
                  <div style={{
                    marginTop: 6, fontSize: 11, color: '#856404',
                    background: '#fff3cd', border: '1px solid #ffc107',
                    borderRadius: 6, padding: '5px 10px',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <i className="ti ti-info-circle" style={{ fontSize: 13 }} />
                    A password is saved. If the connection is failing, re-enter the correct password and save.
                  </div>
                )}
              </Field>
            </div>

            {/* Test button + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
              <button
                onClick={handleTest}
                disabled={testing}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: `1.5px solid ${COLORS.blue}`,
                  background: testing ? `${COLORS.blue}15` : COLORS.blue,
                  color: testing ? COLORS.blue : '#fff',
                  fontSize: 13, fontWeight: 600, cursor: testing ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s',
                }}
              >
                <i className={`ti ${testing ? 'ti-loader-2' : 'ti-plug'}`} style={{ fontSize: 15 }} />
                {testing ? 'Testing…' : 'Test Connection'}
              </button>

              {testStatus && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 13, fontWeight: 600,
                  color: testStatus === 'ok' ? COLORS.green : COLORS.red,
                }}>
                  <i className={`ti ${testStatus === 'ok' ? 'ti-circle-check' : 'ti-circle-x'}`} style={{ fontSize: 16 }} />
                  {testMsg}
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── Section 2: Table & Field Mapping ── */}
          <SectionCard title="Table & Field Mapping" icon="ti ti-table" color={COLORS.green}>
            <p style={{ fontSize: 12, color: COLORS.gray, marginBottom: 16, marginTop: 0 }}>
              Specify which table holds your loyalty customers and map the columns to mobile number and points.
            </p>

            <Field label="Customer Table Name" required>
              {inp('table_name', mapping.table_name, handleMap, 'e.g. customers or loyalty_members')}
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
              <Field label="Mobile Number Column" required>
                {columns.length > 0
                  ? sel('mobile_field', mapping.mobile_field, handleMap, columns, '— select column —')
                  : inp('mobile_field', mapping.mobile_field, handleMap, 'e.g. phone or mobile_no')
                }
              </Field>

              <Field label="Points Column" required>
                {columns.length > 0
                  ? sel('points_field', mapping.points_field, handleMap, columns, '— select column —')
                  : inp('points_field', mapping.points_field, handleMap, 'e.g. points or loyalty_points')
                }
              </Field>

              <Field label="Card ID Column">
                {columns.length > 0
                  ? sel('card_id_field', mapping.card_id_field, handleMap, columns, '— select column —')
                  : inp('card_id_field', mapping.card_id_field, handleMap, 'e.g. card_id or loyalty_card_no')
                }
              </Field>
            </div>

            {columns.length > 0 && (
              <div style={{
                fontSize: 12, color: COLORS.green, display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
              }}>
                <i className="ti ti-circle-check" style={{ fontSize: 14 }} />
                {columns.length} columns fetched from the table — select the correct ones above.
              </div>
            )}

            {!tested && columns.length === 0 && (
              <div style={{
                fontSize: 12, color: COLORS.orange, display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
              }}>
                <i className="ti ti-info-circle" style={{ fontSize: 14 }} />
                Test the connection first to auto-populate column options, or type the column names manually.
              </div>
            )}
          </SectionCard>

          {/* ── Section 3: App Link ── */}
          <SectionCard title="App Link" icon="ti ti-world" color={COLORS.blue}>
            <p style={{ fontSize: 12, color: COLORS.gray, marginBottom: 16, marginTop: 0 }}>
              Store the URL of the external loyalty system web app or portal.
            </p>
            <Field label="App URL">
              {inp('app_link', mapping.app_link, handleMap, 'e.g. https://loyalty.example.com')}
            </Field>
          </SectionCard>

          {/* ── Save ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                padding: '9px 22px', borderRadius: 8, border: '1.5px solid #e8ecf0',
                background: '#fff', color: COLORS.gray, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '9px 22px', borderRadius: 8, border: 'none',
                background: saving ? `${COLORS.green}80` : COLORS.green,
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.15s',
              }}
            >
              <i className="ti ti-device-floppy" style={{ fontSize: 16 }} />
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}
